import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useToast, Toast } from '../../utils/messages';
import ThemeSettings from './Theme';
import ManageProjectsSection from './ManageProjects';
import AuditLogsSection from './AuditLogs';
import LinksSection from './Links';
import ParametersSection from './Parameters';
import AdminToolsSection from './AdminTools';
import ManageUsersSection from './ManageUsers';

type SettingsSection =
  | 'theme'
  | 'projects'
  | 'audit-logs'
  | 'api-links'
  | 'session-types-platforms'
  | 'admin'
  | 'users';

const SettingsPanel: React.FC = () => {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  // Use workspace-specific role if available, otherwise fall back to global role
  const userRole = (currentWorkspace as any)?.role || user?.role || 'user';

  const [activeSection, setActiveSection] = useState<SettingsSection>('theme');
  const { message, setMessage } = useToast();

  return (
    <div className="form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage projects and system settings</p>
        </div>
      </div>

      <Toast message={message} />

      <div className="flex gap-xl items-start">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="flex flex-col gap-sm">
            <button
              onClick={() => setActiveSection('theme')}
              className={`sidebar-item ${activeSection === 'theme' ? 'active' : ''}`}
            >
              🎨 Theme
            </button>
            <button
              onClick={() => setActiveSection('projects')}
              className={`sidebar-item ${activeSection === 'projects' ? 'active' : ''}`}
            >
              📦 Manage Projects
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => setActiveSection('audit-logs')}
                className={`sidebar-item ${activeSection === 'audit-logs' ? 'active' : ''}`}
              >
                📋 Audit Logs
              </button>
            )}
            <button
              onClick={() => setActiveSection('api-links')}
              className={`sidebar-item ${activeSection === 'api-links' ? 'active' : ''}`}
            >
              🔗 Links
            </button>
            <button
              onClick={() => setActiveSection('session-types-platforms')}
              className={`sidebar-item ${
                activeSection === 'session-types-platforms' ? 'active' : ''
              }`}
            >
              ⚙️ Manage Parameters
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => setActiveSection('admin')}
                className={`sidebar-item ${activeSection === 'admin' ? 'active' : ''}`}
              >
                ⚠️ Admin Tools
              </button>
            )}
            {(userRole === 'admin' || userRole === 'manager') && (
              <button
                onClick={() => setActiveSection('users')}
                className={`sidebar-item ${activeSection === 'users' ? 'active' : ''}`}
              >
                👥 Manage Users
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {activeSection === 'theme' && <ThemeSettings />}
          {activeSection === 'projects' && (
            <ManageProjectsSection userRole={userRole} setMessage={setMessage} />
          )}
          {activeSection === 'audit-logs' && userRole === 'admin' && (
            <AuditLogsSection setMessage={setMessage} />
          )}
          {activeSection === 'api-links' && <LinksSection setMessage={setMessage} />}
          {activeSection === 'session-types-platforms' && (
            <ParametersSection setMessage={setMessage} />
          )}
          {activeSection === 'admin' && userRole === 'admin' && (
            <AdminToolsSection setMessage={setMessage} />
          )}
          {activeSection === 'users' && (userRole === 'admin' || userRole === 'manager') && (
            <ManageUsersSection userRole={userRole} setMessage={setMessage} />
          )}
        </div>
      </div>
      
      <div style={{ 
        marginTop: '2rem', 
        paddingTop: '1rem', 
        borderTop: '1px solid var(--grid-line)',
        textAlign: 'center',
        fontSize: '0.875rem',
        color: 'var(--text-muted)'
      }}>
        ExoTest - v1.3.0
      </div>
    </div>
  );
};

export default SettingsPanel;
